/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-11-16 15:58:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { URL } from 'src/constants/links';
import MainMenu from 'src/components/containers/main-menu';
import SearchAnalyticsTabs from 'src/components/views/analytics-v2/search-analytics-tabs';
import TypeFormEmbedded from 'src/components/views/common/typeform-embedded';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import ThemeUpgradeFlow2Notification from 'src/components/containers/common/theme-upgrade-flow-2-notification';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import FreemiumBanner from 'src/components/views/analytics-v2/freemium-banner';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import { getAddonAnalyticInfos } from 'src/helpers/analytics-v2-helpers.js';
import {
  ADDONS_INFO,
  ANALYTICS_ADDON_NAME,
  AI_POWERED_SEARCH_NAME
} from 'src/constants/subscription';
import ProcessAddon from 'src/components/views/subscription-v3/addon/process-addon';
import BannerUpgrade from 'src/components/views/analytics-v2/banner-upgrade';
import PromoteBanner from 'src/components/views/common/promote-banner';
import ChangePlanFromReviewIOModal from 'src/components/views/common/change-plan-from-reviewio-modal';
import * as subscriptionV3Actions from 'src/stores/actions/subscription-v3-actions';

import { getShop } from 'src/helpers';
import { getInitDate } from 'src/helpers/analytics-helpers';
import { FEATURE_IDS } from 'src/constants/theme-upgrade';
import history from 'src/routes/history';
import { routes } from 'src/routes';

import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import TitleHelp from 'src/components/views/common/title-help';
import { FEATURES } from 'src/constants/analytics';

type Props = WithI18nProps;
type State = {
  actionSurvey: boolean,
  youtubeModal: boolean,
  changePlanFromReviewIOModal: boolean
};

const fromDateStateName = 'searchAnalyticsFromDate';
const toDateStateName = 'searchAnalyticsToDate';
const compareStateName = 'searchAnalyticsCompare';
class Analytics extends Component<Props, State> {
  timeoutActionSurvey = undefined;
  templateKey = 'new-search-analytics';
  state = {
    actionSurvey: false,
    modalAddon: false,
    youtubeModal: false,
    modalAddonSearch: false,
    changePlanFromReviewIOModal: false
  };

  componentDidMount() {
    const { changeValue, getAddonStatus, getDetailInvoice, invoice, selectedPlan } = this.props;

    // Get addon status
    getAddonStatus(ANALYTICS_ADDON_NAME);
    getAddonStatus(AI_POWERED_SEARCH_NAME);
    // Get detail invoice
    if (_.isEmpty(invoice) || !invoice) {
      getDetailInvoice();
    }

    const { fromDate, toDate, compare } = getInitDate(
      FEATURES.SEARCH,
      this.templateKey,
      selectedPlan
    );

    changeValue(fromDateStateName, fromDate);
    changeValue(toDateStateName, toDate);
    changeValue(compareStateName, compare);

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.handleLeavePage);
  }

  componentWillUnmount() {
    this.handleLeavePage();

    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.handleLeavePage);
  }

  handleLeavePage = () => {
    // Insert data to local storage
    try {
      const { fromDate, toDate, compare = false } = this.props;
      localStorage.setItem(`${getShop()}-${this.templateKey}-fromDate`, fromDate.toISOString());
      localStorage.setItem(`${getShop()}-${this.templateKey}-toDate`, toDate.toISOString());
      localStorage.setItem(`${getShop()}-${this.templateKey}-compare`, compare.toString());
    } catch (error) {}
  };

  render() {
    const { actionSurvey, youtubeModal } = this.state;
    const {
      selectedPlan,
      statusStore,
      notAllowAddon,
      addonLoading,
      addonStatus,
      addonStatusSearch,
      i18n,
      location,
      invoice,
      featureAnalytics,
      enabledSubscriptionV4,
      enableAISearch,
      useBoltOn
    } = this.props;
    const { subscribed, subscribedBefore, addonInfo } = getAddonAnalyticInfos(
      invoice,
      addonStatus,
      featureAnalytics
    );
    const buttonUnlockContent = ['trial', 'expiredSoon'].includes(statusStore)
      ? 'unlockFeatureForTrial'
      : 'unLockNow';

    const addonRemainingCycles = _.get(addonStatusSearch, 'trialInfo.remainingDays', 14);

    const isSubscribeNowSearch =
      !['trial', 'expiredSoon'].includes(statusStore) && addonRemainingCycles <= 0;

    return (
      <div className="BC-Page BC-Page__SearchAnalytics" onScroll={this.handleScroll}>
        <MainMenu menuId="menu-analytics-search" />
        {!enableAISearch && !useBoltOn && (
          <PromoteBanner
            name="SearchAnalytics"
            feature="Analytics.SearchAnalytics"
            learnMoreAction={this.tryOutButton}
          />
        )}
        <Page title={i18n.translate('AdminHTML.Analytics.AnalyticsV2.searchAnalytics_PageTitle')}>
          <TitleHelp i18n={i18n} hrefUserManual={URL['Link_V2_Analytics_Search_ManualGuide']} />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Installation reminder status banner */}
                {selectedPlan.includes('freemium') && (
                  <FreemiumBanner urlText="Link_V2_Analytics_Search_ManualGuide" />
                )}

                <InstallationRemindStatusBanner />

                <ThemeUpgradeFlow2Notification featureID={FEATURE_IDS.ANALYTICS} />

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />
              </div>
            </Layout.Section>

            <Layout.Section>
              {!subscribed && !addonLoading && !enabledSubscriptionV4 && (
                <BannerUpgrade
                  handleUnlock={() => this.toggleModal('modalAddon')}
                  actionLabel={buttonUnlockContent}
                  disableLockUnLock={notAllowAddon}
                  subscribedBefore={
                    subscribedBefore && !['trial', 'expiredSoon'].includes(statusStore)
                  }
                  days={30}
                />
              )}
              <SearchAnalyticsTabs
                pathname={location.pathname}
                performActionSurvey={this.performActionSurvey}
                openReviewAddonModal={() => this.toggleModal('modalAddon')}
              />
            </Layout.Section>
          </Layout>
        </Page>
        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbedded type="analytics" conditionShowStateChange={actionSurvey} />
        )}
        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['Link_V2_Analytics_Video']}
          onClose={() => this.toggleModal('youtubeModal')}
          title={i18n.translate('AdminHTML.Analytics.Analytics.youtubeModal_Title')}
        />
        {!enabledSubscriptionV4 && (
          <ProcessAddon
            openModalReview={this.state.modalAddon}
            addon={addonInfo}
            hadSubscribed={subscribedBefore}
            handleAfterSubscribeAddon={this.handleCloseStatus}
            handleReviewModal={() => this.toggleModal('modalAddon')}
          />
        )}
        {/* Process addon unlock search */}
        <ProcessAddon
          openModalReview={this.state.modalAddonSearch}
          addon={ADDONS_INFO.find(e => e.addonName === AI_POWERED_SEARCH_NAME)}
          hadSubscribed={isSubscribeNowSearch}
          handleAfterSubscribeAddon={this.handleCloseStatusSearch}
          handleReviewModal={() => this.toggleModal('modalAddonSearch')}
        />
        <ChangePlanFromReviewIOModal
          open={this.state.changePlanFromReviewIOModal}
          onClose={() => this.setState({ changePlanFromReviewIOModal: false })}
        />
      </div>
    );
  }

  // Run survey when user perform action: change tab, view detail, change date and scroll
  performActionSurvey = () => {
    const { actionSurvey } = this.state;

    clearTimeout(this.timeoutActionSurvey);

    this.timeoutActionSurvey = setTimeout(() => {
      this.setState({ actionSurvey: !actionSurvey });
    }, 30000);
  };

  // Handle scroll action
  handleScroll = e => {
    this.performActionSurvey();
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  // Auto reload page
  handleCloseStatus = () => {
    this.props.getAddonStatus(ANALYTICS_ADDON_NAME);
  };

  handleCloseStatusSearch = () => {
    this.props.getAddonStatus(AI_POWERED_SEARCH_NAME);
  };

  tryOutButton = () => {
    if (this.props.useBoltOn) {
      this.setState({ changePlanFromReviewIOModal: true });
      return;
    }

    if (this.props.notAllowAddon) {
      history.push(routes.subscription.plan());
    } else {
      this.toggleModal('modalAddonSearch');
    }
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get current status addon sync
  getAddonStatus: addonName => dispatch(subscriptionV3Actions.getAddonStatus(addonName)),
  getDetailInvoice: () => dispatch(subscriptionV3Actions.getDetailInvoice(ownProps.i18n)),
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value))
});

const mapStateToProps = (state, props) => {
  return {
    statusStore: _.get(state.oauth, 'subscription.status', ''),
    notAllowAddon: _.get(state.oauth, 'subscription.notAllowAddon', false),
    addonLoading: _.get(state.subscriptionV3, 'loading', false),
    useBoltOn: _.get(state.oauth, 'subscription.useBoltOn', false),
    addonStatus: _.get(state.subscriptionV3, `addonStatus.${ANALYTICS_ADDON_NAME}`, {}),
    addonStatusSearch: _.get(state.subscriptionV3, `addonStatus.${AI_POWERED_SEARCH_NAME}`, {}),
    fromDate: _.get(state.analyticsV2, fromDateStateName),
    toDate: _.get(state.analyticsV2, toDateStateName),
    compare: _.get(state.analyticsV2, compareStateName, false),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan'),
    featureAnalytics: _.get(state.oauth.tenantApp, 'settings.analyticV2.settings', {}),
    enabledSubscriptionV4: _.get(
      state.oauth.tenantApp,
      'settings.subscriptionV4.settings.enable',
      false
    ),
    enableAISearch: _.get(
      state.oauth.tenantApp,
      'settings.instantSearch.settings.AI-synonyms',
      false
    )
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Analytics));
